import type { BoardData } from '../model/board';
import { BoardIndex } from '../rules/boardIndex';

/**
 * Canonical solver.
 *
 * Removing an arrow can never create a blocker, therefore a removable arrow stays
 * removable until it is removed, and the restriction of any valid solution to a reachable
 * sub-board is still valid. Greedy removal consequently clears any solvable board, and by
 * always taking the lowest remaining removable id the result is canonical.
 */

export interface SolveTrace {
  /** Removal order; complete when `solved` is true. */
  readonly solution: number[];
  /** Number of legal moves available before each removal. */
  readonly legalCounts: number[];
  /** Arrows unlocked by each removal. */
  readonly unlockCounts: number[];
  readonly solved: boolean;
  readonly remaining: number;
}

export function solveWithTrace(board: BoardData): SolveTrace {
  const index = BoardIndex.fromBoard(board);
  const solution: number[] = [];
  const legalCounts: number[] = [];
  const unlockCounts: number[] = [];

  while (!index.isCleared) {
    const id = index.firstRemovableId();
    if (id < 0) {
      return {
        solution,
        legalCounts,
        unlockCounts,
        solved: false,
        remaining: index.aliveCount,
      };
    }
    legalCounts.push(index.removableCount);
    const unlocked = index.remove(id);
    unlockCounts.push(unlocked.length);
    solution.push(id);
  }

  return { solution, legalCounts, unlockCounts, solved: true, remaining: 0 };
}

export function canonicalSolution(board: BoardData): number[] {
  const trace = solveWithTrace(board);
  if (!trace.solved) {
    throw new Error(`Board is not solvable: ${trace.remaining} arrows remain`);
  }
  return trace.solution;
}

export function isSolvable(board: BoardData): boolean {
  return solveWithTrace(board).solved;
}

/**
 * Wave decomposition: repeatedly remove *every* currently legal arrow at once.
 * The number of waves is the dependency depth of the board and, unlike walking
 * nearest-blocker chains, it is immune to mutually-blocking cycles.
 */
export function computeWaves(board: BoardData): number[][] {
  const index = BoardIndex.fromBoard(board);
  const waves: number[][] = [];

  while (!index.isCleared) {
    const wave = index.removableIds();
    if (wave.length === 0) break;
    for (const id of wave) index.remove(id);
    waves.push(wave);
  }
  return waves;
}

/** Arrows that are legal on the untouched board. */
export function initialRemovable(board: BoardData): number[] {
  return BoardIndex.fromBoard(board).removableIds();
}
